import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import type { CalendarEvent } from 'src/meetings/models';
import { meetingsQueryKey } from 'src/meetings/hooks/useScopedMeetings';
import type { MeetingScope } from 'src/meetings/scope';

type AssignSpeakerParticipant =
  | { participantType: 'CLIENT'; clientId: string }
  | { participantType: 'USER'; userId: string }
  | { participantType: 'EXTERNAL'; name: string; description: string };

// What to do when the person being assigned already speaks on this transcript. Merging shows both
// voices under the one name and cannot be picked apart afterwards; swapping has the two speakers
// exchange names, leaving both as separate voices.
export type SpeakerConflictResolution = 'merge' | 'swap';

export type AssignSpeakerBody = {
  // Every diarised voice behind one adviser-facing speaker, assigned in one request so a
  // failure part-way cannot leave that speaker split across two names.
  speakerLabels: string[];
  participant: AssignSpeakerParticipant;
  // 'ask' assigns nothing if the person already speaks here and answers with a SpeakerConflict.
  onConflict: SpeakerConflictResolution | 'ask';
};

export type AssignedSpeaker = CalendarEvent & {
  // The name the speaker's segments now show.
  speakerName: string;
};

// The speaker already shown under the chosen person's name: who assigning would merge with.
export type SpeakerConflict = {
  personName: string;
  speakerLabels: string[];
};

export function speakerConflictOf(error: unknown): SpeakerConflict | null {
  if (!ApiError.isApiError(ApiErrorCode.SPEAKER_CONFLICT, error)) return null;
  const personName = error.detail?.person_name;
  const speakerLabels = error.detail?.speaker_labels;
  if (typeof personName !== 'string' || !Array.isArray(speakerLabels)) return null;
  return { personName, speakerLabels: speakerLabels.map(String) };
}

export const useAssignSpeaker = (scope: MeetingScope, eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ data: AssignedSpeaker }, Error, AssignSpeakerBody>({
    mutationFn: body =>
      api.post<AssignedSpeaker>(`calendar-events/${eventId}/transcript/assign-speaker`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meetingsQueryKey(scope) });
      // Assigning a speaker can add or remove attendees, renames the transcript's speakers, and
      // puts an existing note out of date. Refetching everything under the event, its meeting
      // notes included, shows all three without a reload.
      queryClient.invalidateQueries({ queryKey: ['calendar-events', eventId] });
    },
  });
};
